// client/src/components/shared/NavBar.js
import React from 'react';

const LS_AVAILABLE_GROUPS = 'intranet_available_groups';
const LS_SELECTED_GROUP = 'intranet_selected_group';
const LS_EXPERIENCE = 'experience';
const LS_PREVIEW = 'intranet_preview';

const GROUP_LABELS = {
  INTRANET_Internal: 'Internal (Production)',
  INTRANET_Seasonal: 'Seasonal (Production)',
  INTRANET_Internal_Preview: 'Internal (Preview)',
  INTRANET_Seasonal_Preview: 'Seasonal (Preview)',
};

function getExperience() {
  const exp = window.localStorage.getItem(LS_EXPERIENCE) || 'internal';
  return String(exp).toLowerCase();
}

function getAvailableGroups() {
  try {
    const raw = window.localStorage.getItem(LS_AVAILABLE_GROUPS);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch (e) {
    return [];
  }
}

function getSelectedGroup() {
  return window.localStorage.getItem(LS_SELECTED_GROUP) || '';
}

function applySelectedGroup(group) {
  const isInternal = group === 'INTRANET_Internal' || group === 'INTRANET_Internal_Preview';
  const isPreview = group && group.endsWith('_Preview');

  try {
    window.localStorage.setItem(LS_SELECTED_GROUP, group);
    window.localStorage.setItem(LS_EXPERIENCE, isInternal ? 'internal' : 'seasonal');
    window.localStorage.setItem(LS_PREVIEW, isPreview ? 'true' : 'false');
  } catch (e) {
    // ignore quota issues
  }

  window.location.reload();
}

function resolveNavLinks(navProp) {
  if (Array.isArray(navProp)) return navProp;

  const nav = navProp || {};
  const experience = getExperience();

  const internal = Array.isArray(nav.primaryNavigationLinks) ? nav.primaryNavigationLinks : [];
  const seasonal = Array.isArray(nav.seasonalPrimaryNavigationLinks) ? nav.seasonalPrimaryNavigationLinks : [];

  if (experience === 'seasonal' && seasonal.length > 0) return seasonal;
  return internal;
}

function MapLinks(props) {
  const links = props.links || [];
  return (
    <ul className="app-sidebar__links">
      {links.map(function (link, index) {
        return (
          <li key={index} className="nav nav-item white">
            <a className="app-sidebar__link" href={link.route}>
              {link.text}
            </a>
          </li>
        );
      })}
    </ul>
  );
}

class NavBar extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      sidebarOpen: false,
      value: '',
      selectedGroup: getSelectedGroup(),
      availableGroups: getAvailableGroups(),
    };
    this.onSetSidebarOpen = this.onSetSidebarOpen.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleModeChange = this.handleModeChange.bind(this);
    this.refreshGroupsFromStorage = this.refreshGroupsFromStorage.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  componentDidMount() {
    // Router resolves Okta groups async and persists them to localStorage.
    // localStorage writes in the same tab do NOT trigger the native "storage" event,
    // so Router dispatches a custom event we can listen for.
    this.refreshGroupsFromStorage();
    window.addEventListener('intranet_groups_updated', this.refreshGroupsFromStorage);
    window.addEventListener('keydown', this.handleKeyDown);
  }

  componentWillUnmount() {
    window.removeEventListener('intranet_groups_updated', this.refreshGroupsFromStorage);
    window.removeEventListener('keydown', this.handleKeyDown);
  }

  refreshGroupsFromStorage() {
    this.setState({
      selectedGroup: getSelectedGroup(),
      availableGroups: getAvailableGroups(),
    });
  }

  handleChange(event) {
    this.setState({ value: event.target.value });
  }

  handleModeChange(event) {
    const group = event.target.value;
    if (!group) return;
    applySelectedGroup(group);
  }

  handleKeyDown(event) {
    if (event.key === 'Escape' && this.state.sidebarOpen) {
      this.onSetSidebarOpen(false);
    }
  }

  onSetSidebarOpen(open) {
    this.setState({ sidebarOpen: open });
  }

  render() {
    const experience = getExperience();
    const showSearch = experience === 'internal';

    const links = resolveNavLinks(this.props.NavBar);

    const { availableGroups, selectedGroup } = this.state;
    const showModeSwitcher = Array.isArray(availableGroups) && availableGroups.length > 1;

    return (
      <div className={this.state.sidebarOpen ? 'app-nav is-open' : 'app-nav'}>
        <div className="app-bar">
          <button
            type="button"
            className="app-bar__menuButton"
            aria-label="Open navigation menu"
            aria-controls="app-sidebar"
            aria-expanded={this.state.sidebarOpen}
            onClick={() => this.onSetSidebarOpen(!this.state.sidebarOpen)}
          >
            <span className="fa fa-bars fa-2x btn menu-bar">
              <span className="title">Menu</span>
            </span>
          </button>

          {showModeSwitcher && (
            <div className="app-bar__mode">
              <span className="app-bar__modeLabel">Mode:</span>
              <select
                className="app-bar__modeSelect"
                value={selectedGroup || ''}
                onChange={this.handleModeChange}
              >
                <option value="" disabled>
                  Select...
                </option>
                {availableGroups.map((g) => (
                  <option key={g} value={g}>
                    {GROUP_LABELS[g] || g}
                  </option>
                ))}
              </select>
            </div>
          )}

          {showSearch && (
            <div className="row app-bar__searchRow">
              <div className="col app-bar__searchCol">
                <div id="search-box" className="search-bar">
                  <form id="search-form" action="/search-results">
                    <div>
                      <label htmlFor="query" className="assistive-text">
                        Search
                      </label>
                      <input
                        type="search"
                        id="query"
                        name="queryStr"
                        placeholder="Find Employee/Search Site"
                        value={this.state.value}
                        onChange={this.handleChange}
                        maxLength="150"
                        className="black"
                      />
                      <button type="submit" className="btn btn-primary" aria-label="Search">
                        <span className="fa fa-search white"></span>
                        <span className="assistive-text">
                          Go <span className="fa fa-caret-right"></span>
                        </span>
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}
        </div>

        {this.state.sidebarOpen && (
          <button
            type="button"
            className="app-nav__overlay"
            aria-label="Close navigation menu"
            onClick={() => this.onSetSidebarOpen(false)}
          />
        )}

        <nav
          id="app-sidebar"
          className="app-sidebar"
          aria-label="Primary navigation"
          aria-hidden={!this.state.sidebarOpen}
        >
          <MapLinks links={links} />
        </nav>
      </div>
    );
  }
}

export default NavBar;
